import React, { useRef, useState, useCallback } from "react";
import {
    intl,
    DmsAltBox,
    DmsPage,
    DmsTable,
    DmsButton,
    DmsFlexContainer,
    fetchData,
} from "dmsbasereact";
import Swal from "sweetalert2";
import { Typography, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatCnpj } from "../../utils/formatters";
import { PartnerLinkModal } from "../../components/PartnerLinkModal";
import { getInstitutionCategoryOptions, getInstitutionCategoryLabel } from "../../constants/InstitutionType";

export const PartnerPage = () => {
    const table = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);

    const handleOpenCreate = useCallback(() => {
        setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
    }, []);

    const handleSave = useCallback(() => {
        if (table.current) {
            table.current.updateTable();
        }
    }, []);

    const handleUnlink = useCallback(async (row) => {
        const result = await Swal.fire({
            icon: "warning",
            title: intl.get("ConfirmUnlinkPartner").d("Are you sure you want to unlink this partner?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (result.isConfirmed) {
            try {
                await fetchData(
                    `/partner/v2/link/${row.distributor_ob_pk}/${row.id}`,
                    "DELETE",
                    true
                );
                Swal.fire({
                    icon: "success",
                    title: intl.get("PartnerUnlinkedSuccessfully").d("Partner unlinked successfully"),
                });
                if (table.current) {
                    table.current.updateTable();
                }
            } catch {
                Swal.fire({
                    icon: "error",
                    title: intl.get("FailedToUnlinkPartner").d("Failed to unlink partner"),
                });
            }
        }
    }, []);

    const header = [
        {
            label: intl.get("DistributorName").d("Distributor Name"),
            column_name: "distributor_name",
            filter: true,
            sortable: true,
            properties: { filter: { field: "distributor_name" } },
        },
        {
            label: intl.get("InstitutionName").d("Institution Name"),
            column_name: "institution_name",
            filter: true,
            sortable: true,
            properties: { filter: { field: "institution_name" } },
        },
        {
            label: intl.get("CNPJ").d("CNPJ"),
            column_name: "cnpj",
            filter: true,
            sortable: true,
            properties: { filter: { field: "cnpj" } },
            function: (value) => formatCnpj(value),
        },
        {
            label: intl.get("Category").d("Category"),
            column_name: "category",
            filter: true,
            control: "select",
            options: getInstitutionCategoryOptions(),
            sortable: false,
            function: (value) => getInstitutionCategoryLabel(value) || "-",
        },
        {
            label: intl.get("Address").d("Address"),
            column_name: "address",
            sortable: false,
        },
        {
            label: intl.get("ZipCode").d("Zip Code"),
            column_name: "zip_code",
            sortable: false,
        },
        {
            label: intl.get("City").d("City"),
            column_name: "city",
            sortable: false,
        },
        {
            label: intl.get("State").d("State"),
            column_name: "state",
            sortable: false,
            width: "80px",
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            sortable: false,
            width: "80px",
            element: (row) => (
                <Tooltip title={intl.get("Unlink").d("Unlink")}>
                    <IconButton
                        size="small"
                        onClick={() => handleUnlink(row)}
                        color="error"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    const toolbar = (
        <DmsFlexContainer>
            <DmsFlexContainer.Left>
                <DmsButton
                    icon={<AddIcon />}
                    text={intl.get("CreateDistributorClientLink").d("Create Distributor Client Link")}
                    onClick={handleOpenCreate}
                    variant="primary"
                />
            </DmsFlexContainer.Left>
        </DmsFlexContainer>
    );

    return (
        <DmsPage name="Partner" message={intl.get("PartnerPage.PageAccess").d("User accessed Partners page")}>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                        {intl.get("Partner").d("Partner")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTable
                        header={header}
                        managed={true}
                        needs_authentication={true}
                        ref={table}
                        url="/partner/table"
                        toolbar={toolbar}
                        export_table={true}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            <PartnerLinkModal
                isVisible={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
            />
        </DmsPage>
    );
};

export default PartnerPage;
